// # 시각·날짜·수를 화면 언어로
//
// `toLocaleString(undefined)` 는 **브라우저** 언어를 따른다. 화면을 한국어로 골라도 영어
// 브라우저면 `10:00 AM`·`September 25` 가 섞인다. 여기 함수들은 `locale_now()`(화면 언어)를
// 따르고, 시험을 위해 언어를 마지막 인자로 받는다.
//
// 형식기는 만들기가 비싸서 언어·모양마다 하나만 두고 다시 쓴다.

import { bcp47, type Locale, locale_now, msg } from './i18n.ts'

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>()

function dtf(locale: Locale, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d${locale}${JSON.stringify(opts)}`
  let f = cache.get(key)
  if (f === undefined) {
    f = new Intl.DateTimeFormat(bcp47(locale), opts)
    cache.set(key, f)
  }
  return f as Intl.DateTimeFormat
}

function nf(locale: Locale, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n${locale}${JSON.stringify(opts)}`
  let f = cache.get(key)
  if (f === undefined) {
    f = new Intl.NumberFormat(bcp47(locale), opts)
    cache.set(key, f)
  }
  return f as Intl.NumberFormat
}

function rtf(locale: Locale): Intl.RelativeTimeFormat {
  const key = `r${locale}`
  let f = cache.get(key)
  if (f === undefined) {
    f = new Intl.RelativeTimeFormat(bcp47(locale), { numeric: 'auto' })
    cache.set(key, f)
  }
  return f as Intl.RelativeTimeFormat
}

type When = Date | string | number

function date_of(when: When): Date {
  return when instanceof Date ? when : new Date(when)
}

/** 시:분. `오후 3:05` · `3:05 PM`. */
export function fmt_time(when: When, locale: Locale = locale_now()): string {
  return dtf(locale, { hour: 'numeric', minute: '2-digit' }).format(date_of(when))
}

/**
 * 월·일(해가 다르면 연도까지). `9월 25일` · `Sep 25` · `2025년 9월 25일`.
 * `now` 는 올해를 가르는 기준이다.
 */
export function fmt_date(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { year: 'numeric', month: 'short', day: 'numeric' }
  return dtf(locale, opts).format(d)
}

/** 요일 붙은 날짜와 시각. 예약·기한처럼 "언제" 가 중요한 자리. `9월 25일 (목) 오후 3:05`. */
export function fmt_datetime(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return dtf(locale, opts).format(d)
}

/**
 * 오늘이면 `오늘 오후 3:05`, 아니면 `9월 25일 오후 3:05`. 예약처럼 가까운 때를 말하는 자리.
 */
export function fmt_when(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  const time = fmt_time(d, locale)
  if (d.toDateString() !== now.toDateString()) return `${fmt_date(d, locale, now)} ${time}`
  const today = rtf(locale).format(0, 'day')
  return `${today.charAt(0).toUpperCase()}${today.slice(1)} ${time}`
}

/**
 * 문장 한가운데 들어가는 `fmt_when` — 영어에서 "until Today 2:10 PM" 처럼 대문자가 서지 않게
 * 오늘을 소문자로 둔다(3차 F332). 홀로 서는 자리(목록의 시각)는 `fmt_when` 그대로다.
 */
export function fmt_when_in(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  if (d.toDateString() !== now.toDateString()) return fmt_when(d, locale, now)
  return `${rtf(locale).format(0, 'day')} ${fmt_time(d, locale)}`
}

/**
 * 메시지 목록의 날 구분선. `오늘`·`어제`·`9월 25일 (목)`(해가 다르면 연도까지).
 * 오늘·어제는 `Intl.RelativeTimeFormat` 이 화면 언어로 준다 — 번역 표에 낱말을 따로 두지 않는다.
 */
export function fmt_day(when: When, locale: Locale = locale_now(), now: Date = new Date()): string {
  const d = date_of(when)
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const back = Math.round((midnight(now) - midnight(d)) / 86_400_000)
  if (back === 0 || back === 1) {
    const word = rtf(locale).format(-back, 'day')
    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`
  }
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', weekday: 'short' }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return dtf(locale, opts).format(d)
}

/**
 * 목록용 날짜·시각. `9월 25일 오후 3:05` · `Sep 25, 3:05 PM`(해가 다르면 연도까지).
 * `dateStyle: 'short'` 는 한국어에서 `25. 9. 25.` 라 읽히지 않는다.
 */
export function fmt_short(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return dtf(locale, opts).format(d)
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * 지금에서 얼마나 떨어졌나. `방금`·`5분 전`·`어제`·`3일 후`. 일주일을 넘으면 날짜로 쓴다 —
 * "32일 전" 은 계산을 사람에게 떠넘긴다.
 */
export function fmt_relative(
  when: When,
  locale: Locale = locale_now(),
  now: Date = new Date(),
): string {
  const d = date_of(when)
  const diff = d.getTime() - now.getTime()
  const abs = Math.abs(diff)
  const r = rtf(locale)
  if (abs < MINUTE) return r.format(0, 'second')
  if (abs < HOUR) return r.format(Math.round(diff / MINUTE), 'minute')
  if (abs < DAY) return r.format(Math.round(diff / HOUR), 'hour')
  if (abs < 7 * DAY) return r.format(Math.round(diff / DAY), 'day')
  return fmt_date(d, locale, now)
}

/** 수. `1,234`. */
export function fmt_num(n: number, locale: Locale = locale_now()): string {
  return nf(locale, {}).format(n)
}

const UNITS = ['KB', 'MB', 'GB', 'TB'] as const

/** 사람이 읽는 크기. 1024 단위, 소수는 한 자리까지. `512 B`·`1.5 MB`. */
export function fmt_bytes(bytes: number, locale: Locale = locale_now()): string {
  if (bytes < 1024) return `${fmt_num(bytes, locale)} B`
  let n = bytes / 1024
  let u = 0
  while (n >= 1024 && u < UNITS.length - 1) {
    n /= 1024
    u += 1
  }
  return `${nf(locale, { maximumFractionDigits: 1 }).format(n)} ${UNITS[u]}`
}

// 알림 일시 중지의 시간표. 설정의 알림 탭과 사이드바 내 메뉴가 **같은 표**를 쓴다 — 두 벌이면 메뉴에는
// "2시간" 이 있고 설정에는 없는 날이 온다. 따로 파일로 떼면 첫 청크와 두 늦은 청크가 나눠 쓰는 새 모듈이
// 되어 Rolldown 이 첫 화면을 여러 조각으로 다시 쪼갠다(+2 kB) — 이미 모두가 나눠 쓰는 여기에 둔다.

/** 슬랙 "Pause notifications" 의 시간 메뉴. 분 단위, `null` 은 "내일 아침 9시". */
export const SNOOZES: readonly (readonly [string, number | null])[] = [
  [msg('30분'), 30],
  [msg('1시간'), 60],
  [msg('2시간'), 120],
  [msg('내일 아침까지'), null],
]

/** 멈춤 끝 시각. "내일 아침" 은 **내 기기 시간**으로 내일 9시다. */
export function snooze_end(now: Date, minutes: number | null): string {
  if (minutes !== null) return new Date(now.getTime() + minutes * 60_000).toISOString()
  const next = new Date(now)
  next.setDate(next.getDate() + 1)
  next.setHours(9, 0, 0, 0)
  return next.toISOString()
}

/** 지금도 멈춰 있는가. 지난 시각은 서버가 읽을 때 지우지만(`Profile::as_of`) 화면은 캐시를 본다. */
export function snoozed(until: string | null | undefined, now = Date.now()): until is string {
  return until != null && new Date(until).getTime() > now
}
